import React from 'react';
import useUrlParams from 'Hooks/useUrlParams';
import { AISummaryStreamParams } from '../useFetchGenericAiSummaryV3';

interface UseAIStreamStateProps {
  syncWithUrl?: boolean;
}

interface UseAIStreamStateReturn {
  streamId?: string;
  parentStreamId?: string;
  isNewStream: boolean;
  setStreamId: (streamId: string) => void;
  clearStreamId: () => void;
  urlParams: AISummaryStreamParams;
  updateUrlParams: (params: Partial<AISummaryStreamParams>) => void;
}

/**
 * Hook for managing AI stream state with unified URL and local state management
 * Provides single source of truth for stream ID handling
 */
export const useAIStreamStateV3 = ({
  syncWithUrl = false,
}: UseAIStreamStateProps): UseAIStreamStateReturn => {
  const { urlParams, updateUrlParams } = useUrlParams<AISummaryStreamParams>();
  const [stateStreamId, setStateStreamId] = React.useState<string | undefined>();

  const streamId = React.useMemo(() => {
    if (syncWithUrl) {
      // Return undefined for both old 'new-stream' format and new 'new-stream-{alias}' format
      return urlParams.aiStream?.startsWith('new-stream') ? undefined : urlParams.aiStream;
    }
    return stateStreamId;
  }, [syncWithUrl, urlParams.aiStream, stateStreamId]);

  const isNewStream = React.useMemo(() => {
    return syncWithUrl && urlParams.aiStream?.startsWith('new-stream');
  }, [syncWithUrl, urlParams.aiStream]);

  const setStreamId = React.useCallback(
    (newStreamId: string) => {
      if (syncWithUrl) {
        updateUrlParams({ aiStream: newStreamId });
      } else {
        setStateStreamId(newStreamId);
      }
    },
    [syncWithUrl, updateUrlParams],
  );

  const clearStreamId = React.useCallback(() => {
    if (syncWithUrl) {
      updateUrlParams({ aiStream: undefined });
    } else {
      setStateStreamId(undefined);
    }
  }, [syncWithUrl, updateUrlParams]);

  return {
    streamId,
    parentStreamId: urlParams.parentStreamId,
    isNewStream,
    setStreamId,
    clearStreamId,
    urlParams,
    updateUrlParams,
  };
};
